import styled from 'styled-components'
import * as d3 from 'd3'
import { Dict } from 'styled-components/dist/types'
import {
  Ref,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import { useWindowSize } from 'usehooks-ts'

type BarChartProps = {
  data: Dict[]
  bodyHeight: number
  gender: number
  youLabel: string
  yLabelM: string
  yLabelW: string
  mean?: number
  time: string
  isMobile: boolean
}

const BarChart = forwardRef(
  (
    {
      data,
      bodyHeight,
      gender,
      youLabel,
      yLabelM,
      yLabelW,
      mean,
      time,
      isMobile,
    }: BarChartProps,
    ref: Ref<ForwardRefObject>
  ) => {
    const svgRef = useRef<SVGSVGElement>()
    const windowDimensions = useWindowSize()
    useImperativeHandle(ref, () => ({ drawChart }))
    function drawChart() {
      if (!svgRef.current || !data) return
      // set the dimensions and margins of the graph
      const margin = { top: 30, right: 10, bottom: 0, left: 10 }
      const width = svgRef.current.clientWidth - margin.left - margin.right
      const height = svgRef.current.clientHeight - margin.top - margin.bottom

      // append the svg object to the body of the page
      const svg = d3.select(svgRef.current)
      svg.selectChildren().remove()
      const chart = svg
        .append('g')
        .attr('translate', `transform(${margin.left},${margin.bottom})`)

      // prepare svg elements
      const baseArrow = '0,16 8,32 16,16'
      const leftArrow = '0,24 16,32 16,16'
      const rightArrow = '0,16 0,32 16,24'

      // set default orientation
      let orientation = 'default'

      // X axis
      const x = d3
        .scaleBand<Number>()
        .range([0, width])
        .domain(data.map((d) => d.Groesse))
        .padding(0.2)

      const xAxis = d3
        .axisBottom(x)
        .tickValues(
          x.domain().filter((d) => {
            return Array.from(
              { length: (210 - 140) / 10 + 1 },
              (_, index) => 140 + index * 10
            ).includes(Number(d))
          })
        )
        .tickSizeOuter(0)
      chart
        .append('g')
        .attr('class', 'x axis-grid') // Added 'x' to class
        .attr('transform', `translate(0, ${height})`)
        .call(xAxis)
        .selectAll('text')
        .style('text-anchor', 'middle')
        .call((g) => {
          g.filter((d, i, nodes) => i === nodes.length - 1).text(
            (d) => `${d} cm`
          )
        })

      // Add Y axis
      const y = d3.scaleLinear().domain([0, 8]).range([height, 0])
      const yAxisGrid = d3.axisLeft(y).tickSize(-width).tickValues([2, 4, 6])
      // .tickFormat(0, 1, 5, '+%')

      chart
        .append('g')
        .attr('class', 'y axis-grid')
        .style('text-anchor', 'start')
        .call(yAxisGrid)
        .call((g) => g.select('.domain').remove())
        .call((g) => {
          g.selectAll('.tick text').attr('x', 0).attr('dy', -4)
          g.select('.tick:last-of-type text').text(function (d) {
            return d + `${gender === 1 ? yLabelM : yLabelW}`
          })
        })

      // Durchscnitt anzeigen
      mean && chart.append('g').attr('class', 'bg-annotations')
      mean &&
        chart
          .select('.bg-annotations')
          .append('line')
          .attr('x1', x(mean)! + x.bandwidth() / 2)
          .attr('x2', x(mean)! + x.bandwidth() / 2)
          .attr('y1', y.range()[1])
          .attr('y2', y.range()[0])
          .attr('stroke', 'var(--text-color)')
          .attr('stroke-width', 2.5)
          .attr('stroke-dasharray', '5,5')

      // Bars
      chart
        .selectAll('mybar')
        .data(data)
        .join('rect')
        .attr('x', (d) => String(x(d.Groesse)))
        .attr('y', function (d) {
          return y(0)
        })
        .attr('width', x.bandwidth())
        .attr('height', function (d) {
          return height - y(0)
        })
        .attr('fill', function (d, i) {
          return d.Groesse === bodyHeight
            ? 'var(--bar-highlight)'
            : gender === 1
            ? 'var(--gender-male)'
            : 'var(--gender-female)'
        })
        .transition()
        .delay(function (d, i) {
          return i * 25
        })
        .duration(400)
        .attr('y', function (d) {
          return y(Number(d.share))
        })
        .attr('height', (d) => height - y(Number(d.share)))

      // Add annotation
      chart.append('g').attr('class', 'fg-annotations')
      let foundData = data.find((d) => d.Groesse === bodyHeight)
      let shareValue = foundData ? foundData.share : 0

      let xVal = x(bodyHeight) || 0
      let yVal = y(shareValue) || 0
      // console.log(xVal + x.bandwidth() / 2 - 8 - 8 < 0)

      // Body height larger than x range?
      if ((bodyHeight as Number) > x.domain()[x.domain().length - 1]) {
        xVal = x(x.domain()[x.domain().length - 2]) || 0
        orientation = 'right'
      }
      // Body height smaller than x range?
      if ((bodyHeight as Number) < x.domain()[1]) {
        xVal = x(x.domain()[2]) || 0
        orientation = 'left'
      }
      if (bodyHeight < 132 && youLabel.length > 3) {
        xVal = x(x.domain()[2]) || 0
        orientation = 'left'
      }
      // Even more strict if mobile
      if (isMobile) {
        if (bodyHeight < 133) {
          xVal = x(x.domain()[2]) || 0
          orientation = 'left'
        }
        if (bodyHeight < 135 && youLabel.length > 3) {
          xVal = x(x.domain()[2]) || 0
          orientation = 'left'
        }
      }

      const labelXOffset =
        orientation === 'left'
          ? youLabel.length > 3
            ? 35
            : 25
          : orientation === 'right'
          ? youLabel.length > 3
            ? -35
            : -25
          : 0
      const labelYOffset =
        orientation === 'left' ? -5 : orientation === 'right' ? -5 : -25

      chart
        .select('.fg-annotations')
        .append('polygon')
        .attr(
          'points',
          orientation === 'left'
            ? leftArrow
            : orientation === 'right'
            ? rightArrow
            : baseArrow
        )
        .attr('fill', 'var(--text-color)')
        .attr(
          'transform',
          `translate(${xVal + x.bandwidth() / 2 - 8},${yVal - 35}) rotate(0)`
        )
        .attr('opacity', '0')
        .transition()
        .delay(500)
        .attr('opacity', '1')
      chart
        .select('.fg-annotations')
        .append('text')
        .attr('class', 'user-annotations annotation-background')
        .attr('x', xVal + x.bandwidth() / 2 + labelXOffset)
        .attr('y', yVal + labelYOffset)
        .text(youLabel)
        .attr('text-anchor', 'middle')
        .attr('opacity', '0')
        .transition()
        .delay(500)
        .attr('opacity', '1')
      chart
        .select('.fg-annotations')
        .append('text')
        .attr('class', 'user-annotations ')
        .attr('x', xVal + x.bandwidth() / 2 + labelXOffset)
        .attr('y', yVal + labelYOffset)
        .text(youLabel)
        .attr('text-anchor', 'middle')
        .attr('opacity', '0')
        .transition()
        .delay(500)
        .attr('opacity', '1')
      chart
        .select('.fg-annotations')
        .append('rect')
        .style('fill', 'var(--site-background)')
        .style('stroke', 'var(--text-color)')
        .attr('rx', 5)
        .attr('y', 2)
        .attr('x', 5)
        .attr('width', time.length > 5 ? 100 : 60)
        .attr('height', 30)
      chart
        .select('.fg-annotations')
        .append('text')
        .style('fill', 'var(--text-color)')
        .attr('x', time.length > 5 ? 55 : 35)
        .attr('y', 24)
        .text(time)
        .attr('text-anchor', 'middle')
    }

    useEffect(() => {
      drawChart()
    }, [windowDimensions.width])

    return (
      <>
        <BarChartContainer>
          <svg ref={svgRef as React.RefObject<SVGSVGElement>} />
        </BarChartContainer>
      </>
    )
  }
)

const BarChartContainer = styled.div`
  height: 100%;
  width: 100%;
  text-align: center;
  font-family: var(--font-sans-serif);
  /* margin-bottom: 40px; */
  svg {
    width: 100%;
    height: 100%;
  }
  .axis-grid line {
    stroke: var(--chart-grid);
    stroke-dasharray: 4, 2;
  }
  .tick text {
    font-size: 14px;
    fill: var(--text-color);
  }
  .user-annotations {
    font-size: 1.2em;
    fill: var(--text-color);
  }
  .annotation-background {
    stroke: var(--site-background);
    stroke-width: 3px;
  }
  // select last .tick and move it to the right by 1ch
  .x > .tick:last-of-type text {
    transform: translateX(1.3ch);
  }
`

export default BarChart
